import { scopedSetter } from "../api/scopedSetter.js";
import { create } from "zustand";
import { useBalances } from "../state/balances.js";
import { decorationById } from "@zend/content";
import { baseApi, researchApi, type BaseState, type ResearchState } from "./api.js";
import { ApiError } from "../api/http.js";
import { useSession } from "../state/session.js";
import { toast } from "../ui/Toast.js";
import { t } from "../i18n/index.js";
import type { BuildingCategory } from "@zend/content";

export type BaseMode =
  | { kind: "view" }
  | { kind: "place"; buildingId: string; w: number; h: number; rot: number; cover?: boolean }
  | { kind: "move"; rowId: string; w: number; h: number; rot: number }
  /** Placing an owned decoration item or moving a placed one; the ghost is the rotated footprint (D70). */
  | { kind: "decorate"; decoId: string; w: number; h: number; rot: number }
  | { kind: "moveDecoration"; id: string; decoId: string; w: number; h: number; rot: number };

/** Which panel is open above the scene; exactly one at a time (mobile-first, R10). */
export type BasePanel =
  | { kind: "none" }
  | { kind: "building"; rowId: string }
  | { kind: "build"; category?: BuildingCategory }
  | { kind: "hq" }
  | { kind: "queue" }
  | { kind: "zones" }
  | { kind: "protocols" }
  | { kind: "research" }
  | { kind: "layouts" }
  | { kind: "decorations" }
  | { kind: "decoration"; id: string };

export interface Ghost {
  x: number;
  y: number;
  valid: boolean;
}

interface BaseStore {
  base: BaseState | null;
  research: ResearchState | null;
  loading: boolean;
  error: string | null;
  mode: BaseMode;
  panel: BasePanel;
  ghost: Ghost | null;
  placing: boolean;
  /** Row id of a building that just completed: the scene plays the burst and clears it. */
  celebrate: string | null;
  /** Row id the camera should tween to. */
  focus: string | null;
  refresh(): Promise<void>;
  refreshResearch(): Promise<void>;
  setMode(mode: BaseMode): void;
  setPanel(panel: BasePanel): void;
  setGhost(ghost: Ghost | null): void;
  setFocus(rowId: string | null): void;
  clearCelebrate(): void;
  select(rowId: string | null): void;
  /** Runs a mutation, then refreshes both the base and the account state (balances, inventory). */
  run<T>(op: () => Promise<T>, okText?: string): Promise<T | null>;
  confirmPlacement(): Promise<void>;
  cancelMode(): void;
  /** Decorate mode for an owned decoration item (or the garden purchase); the placement bar confirms (D70). */
  startDecorate(decoId: string): void;
  startMoveDecoration(id: string): void;
  removeDecoration(id: string): Promise<void>;
  onBuildingPush(payload: { rowId: string; buildingId: string; level: number; state: string }): void;
}

/** True when every cell of the footprint at (x, y) is inside an open rectangle and free of other buildings. */
export function placementValid(base: BaseState, x: number, y: number, w: number, h: number, ignoreRowId?: string, ignoreDecorationId?: string): boolean {
  const rects = [base.grid.core, ...base.zones.filter((z) => z.state === "open").map((z) => z.rect)];
  const inside = (cx: number, cy: number) => rects.some((r) => cx >= r.x && cx < r.x + r.w && cy >= r.y && cy < r.y + r.h);
  for (let i = 0; i < w; i++) for (let j = 0; j < h; j++) if (!inside(x + i, y + j)) return false;
  for (const b of base.buildings) {
    if (b.id === ignoreRowId || b.w === 0) continue;
    if (x < b.x + b.w && x + w > b.x && y < b.y + b.h && y + h > b.y) return false;
  }
  for (const d of base.loop?.decorations ?? []) {
    if (d.id === ignoreDecorationId) continue;
    const dw = d.w ?? 1, dh = d.h ?? 1;
    if (x < d.x + dw && x + w > d.x && y < d.y + dh && y + h > d.y) return false;
  }
  return true;
}

export function errorText(e: unknown): string {
  if (e instanceof ApiError) {
    const key = `base.error.${e.code}`;
    const text = t(key);
    return text === key ? e.detail : text;
  }
  return e instanceof Error ? e.message : String(e);
}

export const useBase = create<BaseStore>((set, get) => ({
  base: null,
  research: null,
  loading: false,
  error: null,
  mode: { kind: "view" },
  panel: { kind: "none" },
  ghost: null,
  placing: false,
  celebrate: null,
  focus: null,
  refresh: async () => {
    const set = scopedSetter<BaseStore>(useBase.setState);
    set({ loading: true });
    try {
      const base = await baseApi.state();
      set({ base, loading: false, error: null });
    } catch (e) {
      set({ loading: false, error: errorText(e) });
    }
  },
  refreshResearch: async () => {
    const set = scopedSetter<BaseStore>(useBase.setState);
    try {
      set({ research: await researchApi.state() });
    } catch (e) {
      set({ error: errorText(e) });
    }
  },
  setMode: (mode) => set({ mode, ghost: null }),
  setPanel: (panel) => set({ panel }),
  setGhost: (ghost) => set({ ghost }),
  setFocus: (focus) => set({ focus }),
  clearCelebrate: () => set({ celebrate: null }),
  select: (rowId) => set({ panel: rowId ? { kind: "building", rowId } : { kind: "none" }, focus: rowId }),
  run: async (op, okText) => {
    const set = scopedSetter<BaseStore>(useBase.setState);
    try {
      const result = await op();
      if (!set.current()) return null;
      await useSession.getState().load();
      if (!set.current()) return null;
      set({ error: null });
      if (get().panel.kind === "research") void get().refreshResearch();
      if (okText) toast.success(okText);
      return result;
    } catch (e) {
      if (set.current()) toast.error(errorText(e));
      return null;
    }
  },
  confirmPlacement: async () => {
    const set = scopedSetter<BaseStore>(useBase.setState);
    const { mode, ghost, base } = get();
    if (!base || !ghost || !ghost.valid || mode.kind === "view" || get().placing) return;
    set({ placing: true });
    try {
      if (mode.kind === "decorate" || mode.kind === "moveDecoration") {
        const res = mode.kind === "decorate"
          ? await get().run(() => baseApi.placeDecoration(mode.decoId, ghost.x, ghost.y, mode.rot), t("base.decorate.placedToast"))
          : await get().run(() => baseApi.moveDecoration(mode.id, ghost.x, ghost.y, mode.rot), t("base.toast.moved"));
        if (res && get().mode === mode) {
          useBalances.getState().setBalances(res.balances, res.collectionRef);
          set({ base: res.base, mode: { kind: "view" }, ghost: null, panel: { kind: "decorations" } });
        }
        return;
      }
      const building = mode.kind === "place"
        ? await get().run(() => baseApi.place(mode.buildingId, ghost.x, ghost.y, mode.rot, mode.cover === true), t("base.toast.placed"))
        : await get().run(() => baseApi.move(mode.rowId, ghost.x, ghost.y, mode.rot), t("base.toast.moved"));
      if (building && get().mode === mode) set({ mode: { kind: "view" }, ghost: null, panel: { kind: "building", rowId: building.id }, focus: building.id });
    } finally {
      if (set.current()) set({ placing: false });
      // Same-account reconnect invalidates results, but must release this operation's controls.
      else if (get().mode === mode) useBase.setState({ placing: false });
    }
  },
  cancelMode: () => set({ mode: { kind: "view" }, ghost: null }),
  startDecorate: (decoId) => {
    const def = decorationById(decoId);
    const base = get().base;
    // Seed the ghost on the first free spot so touch players can confirm or rotate right away, as the build menu does.
    let ghost: Ghost | null = null;
    if (base) {
      const core = base.grid.core;
      outer: for (let y = core.y; y < core.y + core.h; y++) for (let x = core.x; x < core.x + core.w; x++) if (placementValid(base, x, y, def.footprint.w, def.footprint.h)) { ghost = { x, y, valid: true }; break outer; }
      ghost ??= { x: core.x, y: core.y, valid: false };
    }
    set({ mode: { kind: "decorate", decoId, w: def.footprint.w, h: def.footprint.h, rot: 0 }, ghost, panel: { kind: "none" } });
  },
  startMoveDecoration: (id) => {
    const d = get().base?.loop?.decorations.find((x) => x.id === id);
    if (!d) return;
    set({ mode: { kind: "moveDecoration", id, decoId: d.decoId, w: d.w, h: d.h, rot: d.rot }, ghost: { x: d.x, y: d.y, valid: true }, panel: { kind: "none" } });
  },
  removeDecoration: async (id) => {
    const res = await get().run(() => baseApi.removeDecoration(id), t("base.decorate.storedToast"));
    if (res) {
      useBalances.getState().setBalances(res.balances);
      set({ base: res.base });
    }
  },
  onBuildingPush: (payload) => {
    // Completion bursts; a moved timer (assist) only refreshes.
    if (payload.state === "idle") set({ celebrate: payload.rowId });
    void useSession.getState().load();
  },
}));
